import { Command } from 'cmdk';
import { generateDbml } from '@hyphen-db-studio/dbml-generator';
import { generateSql } from '@hyphen-db-studio/sql-generator';
import { downloadTextFile } from '../../lib/export';
import { exportErAsPng, exportErAsSvg } from '../../lib/exportEr';
import { useDiagramViewStore } from '../../store/useDiagramViewStore';
import { useSchemaStore } from '../../store/useSchemaStore';
import { useThemeStore } from '../../store/useThemeStore';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  diagramRef: React.RefObject<HTMLDivElement>;
  onTriggerImport: () => void;
}

export default function CommandPalette({ open, onClose, diagramRef, onTriggerImport }: CommandPaletteProps) {
  const schema = useSchemaStore((s) => s.schema);
  const dbmlSource = useSchemaStore((s) => s.dbmlSource);
  const projectName = useSchemaStore((s) => s.projectName);
  const addTable = useSchemaStore((s) => s.addTable);
  const addGroup = useSchemaStore((s) => s.addGroup);
  const autoLayout = useSchemaStore((s) => s.autoLayout);
  const setSelection = useSchemaStore((s) => s.setSelection);
  const reactFlowInstance = useDiagramViewStore((s) => s.instance);
  const theme = useThemeStore((s) => s.theme);

  function run(fn: () => void) {
    fn();
    onClose();
  }

  return (
    <Command.Dialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      label="Command palette"
      className="fixed left-1/2 top-24 z-50 w-[480px] -translate-x-1/2 overflow-hidden rounded-lg border border-black/10 bg-panel shadow-2xl dark:border-white/10 dark:bg-gray-800"
    >
      <Command.Input
        autoFocus
        placeholder="Type a command or search tables..."
        className="w-full border-b border-gray-200/70 px-4 py-3 text-sm focus:outline-none dark:border-white/10 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
      />
      <Command.List className="max-h-80 overflow-y-auto p-2">
        <Command.Empty className="px-3 py-6 text-center text-sm text-gray-400 dark:text-gray-500">No results found.</Command.Empty>

        <Command.Group heading="Actions" className="text-xs text-gray-400 dark:text-gray-500">
          <Command.Item onSelect={() => run(() => addTable())} className="cursor-pointer rounded px-3 py-2 text-sm text-gray-800 data-[selected=true]:bg-sky-50 dark:text-gray-200 dark:data-[selected=true]:bg-sky-500/20">
            Create Table
          </Command.Item>
          <Command.Item onSelect={() => run(() => addGroup())} className="cursor-pointer rounded px-3 py-2 text-sm text-gray-800 data-[selected=true]:bg-sky-50 dark:text-gray-200 dark:data-[selected=true]:bg-sky-500/20">
            Create Group
          </Command.Item>
          <Command.Item onSelect={() => run(onTriggerImport)} className="cursor-pointer rounded px-3 py-2 text-sm text-gray-800 data-[selected=true]:bg-sky-50 dark:text-gray-200 dark:data-[selected=true]:bg-sky-500/20">
            Import Schema
          </Command.Item>
          <Command.Item onSelect={() => run(() => downloadTextFile(`${projectName}.dbml`, dbmlSource || generateDbml(schema)))} className="cursor-pointer rounded px-3 py-2 text-sm text-gray-800 data-[selected=true]:bg-sky-50 dark:text-gray-200 dark:data-[selected=true]:bg-sky-500/20">
            Export DBML
          </Command.Item>
          <Command.Item onSelect={() => run(() => downloadTextFile(`${projectName}.sql`, generateSql(schema)))} className="cursor-pointer rounded px-3 py-2 text-sm text-gray-800 data-[selected=true]:bg-sky-50 dark:text-gray-200 dark:data-[selected=true]:bg-sky-500/20">
            Generate SQL
          </Command.Item>
          <Command.Item
            onSelect={() =>
              run(async () => {
                await exportErAsPng(schema, `${projectName}.png`, theme);
              })
            }
            className="cursor-pointer rounded px-3 py-2 text-sm text-gray-800 data-[selected=true]:bg-sky-50 dark:text-gray-200 dark:data-[selected=true]:bg-sky-500/20"
          >
            Export Diagram (PNG)
          </Command.Item>
          <Command.Item
            onSelect={() =>
              run(async () => {
                await exportErAsSvg(schema, `${projectName}.svg`, theme);
              })
            }
            className="cursor-pointer rounded px-3 py-2 text-sm text-gray-800 data-[selected=true]:bg-sky-50 dark:text-gray-200 dark:data-[selected=true]:bg-sky-500/20"
          >
            Export Diagram (SVG)
          </Command.Item>
          <Command.Item onSelect={() => run(autoLayout)} className="cursor-pointer rounded px-3 py-2 text-sm text-gray-800 data-[selected=true]:bg-sky-50 dark:text-gray-200 dark:data-[selected=true]:bg-sky-500/20">
            Auto Layout
          </Command.Item>
          <Command.Item onSelect={() => run(() => useDiagramViewStore.getState().instance?.fitView({ duration: 300 }))} className="cursor-pointer rounded px-3 py-2 text-sm text-gray-800 data-[selected=true]:bg-sky-50 dark:text-gray-200 dark:data-[selected=true]:bg-sky-500/20">
            Fit Diagram
          </Command.Item>
        </Command.Group>

        <Command.Group heading="Tables" className="mt-2 text-xs text-gray-400 dark:text-gray-500">
          {schema.tables.map((table) => (
            <Command.Item
              key={table.id}
              value={table.name}
              onSelect={() => run(() => setSelection({ type: 'table', id: table.id }))}
              className="cursor-pointer rounded px-3 py-2 text-sm text-gray-800 data-[selected=true]:bg-sky-50 dark:text-gray-200 dark:data-[selected=true]:bg-sky-500/20"
            >
              {table.name}
            </Command.Item>
          ))}
        </Command.Group>
      </Command.List>
    </Command.Dialog>
  );
}
